import { CourseMaterial } from "./coursesLoader.ts";
import { Dialog } from "../../../../common/core-ui/Dialog.tsx";
import AssetCover from "common/features/asset/AssetCover.tsx";
import styles from "./AccessCodeDialog.module.scss";
import DisplayNotes from "./common/DisplayNotes.tsx";
import { FormattedMessage, useIntl } from "react-intl";
import BaseLinkOrButton from "common/core-ui/BaseLinkOrButton.tsx";
import clsx from "clsx";
import { submitVitalSourceSupport } from "../common/links.ts";
import CopyInputWithButton from "common/core-ui/CopyInputWithButton.tsx";
import { ReactNode, useEffect } from "react";
import { useAccessCodeFetcher } from "../actions/fetchAccessCodeAction.ts";
import DelayedDotLoader from "../../../../common/core-ui/DelayedDotLoader.tsx";

type MaterialProps = Pick<CourseMaterial, "title" | "displayNotes" | "vbid">;

function AccessCodeInner({
  accessCode,
  url,
}: {
  accessCode: string;
  url: string;
}) {
  const intl = useIntl();

  const showAccessCode = () => (
    <li className={clsx(styles.listItem, styles.statement)}>
      <div className={styles.addSpaceBetween}>
        {intl.formatMessage({ defaultMessage: "Copy the access code." })}
      </div>
      <div
        className={clsx(styles.accessCodeContainer, styles.expandIntoMargin)}
      >
        <CopyInputWithButton
          textToCopy={accessCode}
          label={intl.formatMessage({ defaultMessage: "Access Code" })}
        />
      </div>
    </li>
  );

  const urlButton = (className?: string) => (
    <div className={className}>
      <BaseLinkOrButton
        className={clsx(
          "vst-button-pill",
          "vst-button-variant-outline",
          styles.urlButton
        )}
        href={url}
      >
        {intl.formatMessage({ defaultMessage: "Go to Website" })}
      </BaseLinkOrButton>
    </div>
  );

  const accessCodeAndUrl = () => (
    <ol>
      {showAccessCode()}
      <li className={clsx(styles.listItem, styles.statement)}>
        <div className={styles.addSpaceBetween}>
          {intl.formatMessage({
            defaultMessage:
              "Visit the publisher or third party website to redeem your access code and start using your materials.",
          })}
        </div>
        {urlButton(styles.expandIntoMargin)}
      </li>
    </ol>
  );

  const urlOnly = () => (
    <>
      <div className={styles.statement}>
        {intl.formatMessage({
          defaultMessage:
            "Use this link to visit the publisher or third-party website to start using your materials.",
        })}
      </div>
      {urlButton()}
    </>
  );

  const accessCodeOnly = () => (
    <ol>
      {showAccessCode()}
      <li className={clsx(styles.listItem, styles.statement)}>
        <FormattedMessage
          defaultMessage="Code must be redeemed on a publisher's or third party website. If you are unable to find correct URL, please contact <button>Support</button>*"
          values={{
            button: (text) => (
              <BaseLinkOrButton
                href={submitVitalSourceSupport()}
                className="vst-link vst-link-size-inherit"
              >
                {text}
              </BaseLinkOrButton>
            ),
          }}
        />
      </li>
    </ol>
  );

  return (
    <>
      {accessCode && url && accessCodeAndUrl()}
      {!accessCode && url && urlOnly()}
      {accessCode && !url && accessCodeOnly()}
    </>
  );
}

function AccessCodeDialogFrame({
  title,
  displayNotes,
  vbid,
  onRequestClose,
  children,
}: MaterialProps & {
  onRequestClose: () => void;
  children: ReactNode;
}) {
  const intl = useIntl();

  return (
    <Dialog onRequestClose={onRequestClose}>
      <div className={styles.header}>
        <div className={styles.cover}>
          <AssetCover vbid={vbid} width={60} />
        </div>
        <h1 className={styles.title}>{title}</h1>
      </div>
      {displayNotes && (
        <div>
          <h2 className={styles.instructions}>
            {intl.formatMessage({ defaultMessage: "Instructions" })}
          </h2>
          <DisplayNotes displayNotes={displayNotes} />
        </div>
      )}
      {children}
    </Dialog>
  );
}

export default function AccessCodeDialog({
  fetchUrl,
  onRequestClose,
  title,
  displayNotes,
  vbid,
}: MaterialProps & {
  fetchUrl: string;
  onRequestClose: () => void;
}) {
  const accessCodeFetcher = useAccessCodeFetcher();

  useEffect(() => {
    if (accessCodeFetcher.state === "idle" && !accessCodeFetcher.data) {
      void accessCodeFetcher.submit(
        { url: fetchUrl },
        {
          action: `/my-courses/access-code`,
          method: "PUT",
        }
      );
    }
  }, [accessCodeFetcher, fetchUrl, onRequestClose]);

  function getContent() {
    if (accessCodeFetcher.data && !accessCodeFetcher.data.ok) {
      return (
        <div className="vst-inline-alert">
          <FormattedMessage defaultMessage="Your Access Code could not be retrieved. Please try again." />
        </div>
      );
    }
    if (accessCodeFetcher.data?.data) {
      return (
        <AccessCodeInner
          accessCode={accessCodeFetcher.data.data.code}
          url={accessCodeFetcher.data.data.href}
        />
      );
    }
    return <DelayedDotLoader />;
  }

  return (
    <AccessCodeDialogFrame
      vbid={vbid}
      title={title}
      displayNotes={displayNotes}
      onRequestClose={onRequestClose}
    >
      {getContent()}
    </AccessCodeDialogFrame>
  );
}
